import { useState, type KeyboardEvent } from "react";

interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  variant?: "default" | "warn";
  maxItems?: number;
  maxLength?: number;
}

export function ChipInput({
  value,
  onChange,
  placeholder = "Add a phrase and press Enter",
  variant = "default",
  maxItems = 30,
  maxLength = 120,
}: ChipInputProps) {
  const [draft, setDraft] = useState("");

  function commit() {
    const t = draft.trim();
    if (!t) return;
    if (value.includes(t)) {
      setDraft("");
      return;
    }
    if (value.length >= maxItems) return;
    onChange([...value, t.slice(0, maxLength)]);
    setDraft("");
  }

  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      remove(value.length - 1);
    }
  }

  const chipBg =
    variant === "warn"
      ? "bg-red-50 text-red-700 border-red-100"
      : "bg-ink-100 text-ink-700 border-transparent";

  return (
    <div className="rounded-lg border border-ink-300 bg-white p-2 focus-within:border-accent focus-within:ring-1 focus-within:ring-accent">
      <div className="flex flex-wrap gap-1.5">
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${chipBg}`}
          >
            <span className="max-w-[200px] truncate">{v}</span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-ink-500 hover:bg-ink-200 hover:text-ink-900"
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ""}
          maxLength={maxLength}
          className="min-w-[120px] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm focus:outline-none"
          disabled={value.length >= maxItems}
        />
      </div>
      {value.length >= maxItems ? (
        <p className="mt-1 text-xs text-ink-500">Max {maxItems} items.</p>
      ) : null}
    </div>
  );
}
